import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, ShieldCheck, LogOut, KeyRound } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLock } from '../../contexts/LockContext';
import { userApi } from '../../services/cloudApi';
import Username from '../../components/Common/Username';
import i18n from '../../i18n';

const PIN_INPUT_CLASS =
  'w-full px-4 py-3.5 text-center text-2xl tracking-[0.5em] rounded-xl ' +
  'border border-white/10 bg-[#161f31] text-[var(--color-text-primary)] ' +
  'placeholder-[var(--color-text-muted)] ' +
  'focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/40 focus:border-[#3d8ef0]/60 ' +
  'transition-all duration-200 disabled:opacity-50';

/** Stable shell — must NOT be defined inside LockPage (remounts on every keystroke). */
const LockShell = ({ children }) => (
  <div className="fixed inset-0 z-[999999] flex items-center justify-center bg-[#080e1a]">
    <div
      className="absolute inset-0 opacity-[0.025]"
      style={{
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }}
    />
    <motion.div
      initial={{ opacity: 0, scale: 0.97, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="relative w-full max-w-sm mx-4"
    >
      <div className="bg-[var(--color-bg-base)] rounded-2xl border border-white/[0.07] shadow-[0_24px_64px_rgba(0,0,0,0.6)] p-8">
        {children}
      </div>
    </motion.div>
  </div>
);

const LockIconBadge = () => (
  <div className="flex justify-center mb-6">
    <div className="relative">
      <div className="w-16 h-16 rounded-2xl bg-[#2563eb]/10 border border-[#3d8ef0]/20 flex items-center justify-center">
        <Lock className="w-7 h-7 text-[#3d8ef0]" />
      </div>
      <div className="absolute inset-0 rounded-2xl bg-[#2563eb]/5 blur-xl pointer-events-none" />
    </div>
  </div>
);

const digitsOnly = (value, max) => String(value || '').replace(/\D/g, '').slice(0, max);

const LockPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('lock');
  const { user, authToken, logout } = useAuth();
  const { hasPin, isCheckingPin, isLocked, lockHydrated, verifyPin, setPin: setPinInDb, unlock } =
    useLock();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSettingPin, setIsSettingPin] = useState(false);
  const [forgotStep, setForgotStep] = useState('idle');
  const [otpCode, setOtpCode] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');

  // One-time focus target when a step first opens (never on value change)
  const stepFocusRef = useRef(null);
  const focusedStepRef = useRef(null);

  const ready = Boolean(user && !isCheckingPin && lockHydrated && isLocked);
  const activeStep = !ready
    ? null
    : isSettingPin && forgotStep === 'idle'
      ? 'setup'
      : forgotStep === 'confirm'
        ? 'forgot'
        : 'unlock';

  useEffect(() => {
    if (!user?.id) {
      navigate('/login');
      return;
    }
    if (lockHydrated && !isLocked) {
      navigate('/notes', { replace: true });
    }
  }, [user?.id, lockHydrated, isLocked, navigate]);

  useEffect(() => {
    if (!isCheckingPin) setIsSettingPin(!hasPin);
  }, [hasPin, isCheckingPin]);

  // Focus primary input only when the step identity changes — not on each keypress
  useEffect(() => {
    if (!activeStep) return;
    if (focusedStepRef.current === activeStep) return;
    focusedStepRef.current = activeStep;
    const id = requestAnimationFrame(() => {
      stepFocusRef.current?.focus?.({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [activeStep]);

  const clearError = () => {
    if (error) setError('');
  };

  const handleSetPin = async (e) => {
    e.preventDefault();
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      setError(t('errors.pinFormat'));
      return;
    }
    if (pin !== confirmPin) {
      setError(t('errors.pinMismatch'));
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const result = await setPinInDb(pin);
      if (result?.ok) {
        setPin('');
        setConfirmPin('');
        setIsSettingPin(false);
        setTimeout(() => unlock(), 500);
      } else {
        setError(result?.error === 'INVALID_PIN' ? t('errors.pinFormat') : t('errors.pinSaveFailed'));
      }
    } catch {
      setError(t('errors.pinSaveFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      setError(t('errors.pinFormat'));
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const result = await verifyPin(pin);
      if (result?.ok && result.verified) {
        setPin('');
        unlock();
        return;
      }
      if (result?.error === 'PIN_VERIFY_LOCKED') {
        const mins = Math.ceil((result.retryAfterSeconds || 900) / 60);
        setError(t('errors.tooManyAttempts', { minutes: mins }));
        return;
      }
      if (result?.remainingAttempts != null) {
        setError(t('errors.pinIncorrectRemaining', { count: result.remainingAttempts }));
      } else {
        setError(t('errors.pinIncorrect'));
      }
    } catch {
      setError(t('errors.unlockFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotRequest = async () => {
    if (!authToken) {
      setError(t('errors.sessionExpired'));
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const result = await userApi.requestPinResetCode({
        token: authToken,
        locale: i18n.language,
      });
      if (result?.ok) {
        focusedStepRef.current = null; // allow focus when entering forgot step
        setForgotStep('confirm');
        setError('');
      } else {
        setError(
          result?.error === 'CODE_ALREADY_SENT' || result?.error === 'CODE_ALREADY_SENT_INFO'
            ? t('forgot.codeAlreadySent')
            : t('forgot.sendFailed')
        );
      }
    } catch (err) {
      const code = err?.payload?.error;
      setError(
        code === 'CODE_ALREADY_SENT' || code === 'CODE_ALREADY_SENT_INFO'
          ? t('forgot.codeAlreadySent')
          : t('forgot.sendFailed')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotConfirm = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) {
      setError(t('forgot.codeRequired'));
      return;
    }
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) {
      setError(t('errors.pinFormat'));
      return;
    }
    if (newPin !== confirmNewPin) {
      setError(t('errors.pinMismatch'));
      return;
    }
    if (!authToken) {
      setError(t('errors.sessionExpired'));
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const result = await userApi.confirmPinReset({
        token: authToken,
        code: otpCode.trim(),
        newPin,
      });
      if (result?.ok) {
        setForgotStep('idle');
        setOtpCode('');
        setNewPin('');
        setConfirmNewPin('');
        unlock();
      } else {
        setError(t(`forgot.errors.${result?.error}`, { defaultValue: t('forgot.confirmFailed') }));
      }
    } catch (err) {
      const code = err?.payload?.error || 'confirmFailed';
      setError(t(`forgot.errors.${code}`, { defaultValue: t('forgot.confirmFailed') }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await logout({
        silent: false,
        onComplete: () => navigate('/login', { replace: true }),
      });
    } catch {
      navigate('/login', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  if (!ready) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#080e1a]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#3d8ef0] border-t-transparent mx-auto" />
          <p className="mt-4 text-[var(--color-text-secondary)] text-sm">{t('loading')}</p>
        </div>
      </div>
    );
  }

  const logoutButton = (
    <button
      type="button"
      onClick={handleLogout}
      disabled={isLoading}
      className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-[var(--color-text-muted)] hover:text-red-400 transition-colors disabled:opacity-50"
    >
      <LogOut className="w-3.5 h-3.5" />
      {t('logout')}
    </button>
  );

  /* First-time PIN setup */
  if (activeStep === 'setup') {
    return (
      <LockShell>
        <LockIconBadge />
        <h1 className="text-xl font-bold text-center text-[var(--color-text-primary)] mb-1">
          {t('setup.title')}
        </h1>
        <p className="text-sm text-center text-[var(--color-text-muted)] mb-5">{t('setup.subtitle')}</p>
        <div className="text-center mb-6">
          <span className="text-sm text-[var(--color-text-secondary)]">
            <Username user={user} variant="default" />
          </span>
        </div>
        <form onSubmit={handleSetPin} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[#8aaac8] mb-1.5 tracking-widest uppercase">
              {t('setup.pinLabel')}
            </label>
            <input
              ref={stepFocusRef}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoComplete="off"
              value={pin}
              onChange={(e) => {
                setPin(digitsOnly(e.target.value, 4));
                clearError();
              }}
              placeholder="••••"
              className={PIN_INPUT_CLASS}
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#8aaac8] mb-1.5 tracking-widest uppercase">
              {t('setup.pinConfirmLabel')}
            </label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoComplete="off"
              value={confirmPin}
              onChange={(e) => {
                setConfirmPin(digitsOnly(e.target.value, 4));
                clearError();
              }}
              placeholder="••••"
              className={PIN_INPUT_CLASS}
              disabled={isLoading}
            />
          </div>
          {error ? (
            <p className="text-sm text-red-400 text-center">{error}</p>
          ) : null}
          <button
            type="submit"
            disabled={isLoading || pin.length !== 4 || confirmPin.length !== 4}
            className="w-full mt-2 py-3 bg-[#2563eb] hover:bg-[#3d8ef0] text-white font-semibold rounded-xl transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isLoading ? t('setup.submitting') : t('setup.submit')}
          </button>
        </form>
        {logoutButton}
      </LockShell>
    );
  }

  /* Forgot PIN — OTP + new PIN (same screen; each input keeps its own focus) */
  if (activeStep === 'forgot') {
    return (
      <LockShell>
        <LockIconBadge />
        <h1 className="text-xl font-bold text-center text-[var(--color-text-primary)] mb-1">
          {t('forgot.title')}
        </h1>
        <p className="text-sm text-center text-[var(--color-text-muted)] mb-5">{t('forgot.codeSentHint')}</p>
        <form onSubmit={handleForgotConfirm} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-[#8aaac8] mb-1.5 tracking-widest uppercase">
              {t('forgot.otpLabel')}
            </label>
            <input
              ref={stepFocusRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              value={otpCode}
              onChange={(e) => {
                setOtpCode(digitsOnly(e.target.value, 8));
                clearError();
              }}
              className={PIN_INPUT_CLASS}
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#8aaac8] mb-1.5 tracking-widest uppercase">
              {t('forgot.newPinLabel')}
            </label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoComplete="off"
              value={newPin}
              onChange={(e) => {
                setNewPin(digitsOnly(e.target.value, 4));
                clearError();
              }}
              className={PIN_INPUT_CLASS}
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#8aaac8] mb-1.5 tracking-widest uppercase">
              {t('forgot.newPinConfirmLabel')}
            </label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              autoComplete="off"
              value={confirmNewPin}
              onChange={(e) => {
                setConfirmNewPin(digitsOnly(e.target.value, 4));
                clearError();
              }}
              className={PIN_INPUT_CLASS}
              disabled={isLoading}
            />
          </div>
          {error ? (
            <p className="text-sm text-red-400 text-center">{error}</p>
          ) : null}
          <button
            type="submit"
            disabled={isLoading || !otpCode || newPin.length !== 4 || confirmNewPin.length !== 4}
            className="w-full mt-2 py-3 bg-[#2563eb] hover:bg-[#3d8ef0] text-white font-semibold rounded-xl disabled:opacity-40"
          >
            {isLoading ? t('forgot.submitting') : t('forgot.submitNewPin')}
          </button>
          <button
            type="button"
            onClick={() => {
              focusedStepRef.current = null;
              setForgotStep('idle');
              setError('');
              setOtpCode('');
              setNewPin('');
              setConfirmNewPin('');
            }}
            className="w-full text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
          >
            {t('forgot.back')}
          </button>
        </form>
        {logoutButton}
      </LockShell>
    );
  }

  /* Unlock */
  return (
    <LockShell>
      <LockIconBadge />
      <h1 className="text-xl font-bold text-center text-[var(--color-text-primary)] mb-1">
        {t('unlock.title')}
      </h1>
      <p className="text-sm text-center text-[var(--color-text-muted)] mb-5">{t('unlock.subtitle')}</p>
      <div className="flex flex-col items-center gap-2 mb-6">
        {user?.avatarUrl || user?.avatar_url ? (
          <img
            src={user.avatarUrl || user.avatar_url}
            alt=""
            className="w-10 h-10 rounded-full object-cover border-2 border-white/10"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#161f31] border border-white/10 flex items-center justify-center">
            <span className="text-[var(--color-text-secondary)] font-semibold text-sm">
              {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <span className="text-sm text-[var(--color-text-secondary)]">
          <Username user={user} variant="default" />
        </span>
      </div>
      <form onSubmit={handleUnlock} className="space-y-3">
        <input
          ref={stepFocusRef}
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={4}
          autoComplete="off"
          value={pin}
          onChange={(e) => {
            setPin(digitsOnly(e.target.value, 4));
            clearError();
          }}
          placeholder="••••"
          className={PIN_INPUT_CLASS}
          disabled={isLoading}
        />
        {error ? (
          <p className="text-sm text-red-400 text-center">{error}</p>
        ) : null}
        <button
          type="submit"
          disabled={isLoading || pin.length !== 4}
          className="w-full mt-2 py-3 bg-[#2563eb] hover:bg-[#3d8ef0] text-white font-semibold rounded-xl transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t('unlock.submitting')}
            </>
          ) : (
            <>
              <ShieldCheck className="w-4 h-4" />
              {t('unlock.submit')}
            </>
          )}
        </button>
      </form>
      <button
        type="button"
        onClick={handleForgotRequest}
        disabled={isLoading}
        className="mt-3 w-full flex items-center justify-center gap-2 text-sm text-[#3d8ef0] hover:text-[#5ca3f5] disabled:opacity-50"
      >
        <KeyRound className="w-3.5 h-3.5" />
        {t('forgot.link')}
      </button>
      {logoutButton}
    </LockShell>
  );
};

export default LockPage;
